// @ts-check
import BaseAPI from './BaseAPI';

/**
 * @typedef {object} OrganizationData
 * @property {string} name
 * @property {string} description
 * @property {string} parentId
 * @property {string} templateId
 * @property {string[]} assetIds
 * @property {string[]} adminIds
 */

class OrganizationApi extends BaseAPI {

  // OrganizationData contains initial data for an organization, including the parent id, which is the orgId
  // of the parent organization. The organization is created based on the organization template with the id of templateId.
  // The organization template contains the default privileges, default admins, default roles, and also which models, dashboards,
  // alarms, providers that shall be linked to the new organization.
  // It can also contain a list of assets that should be linked to the new organization.
  /**
   * Create a new sub organization
   * @param {AbortSignal} cancelSignal - {@link https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal
   * @param {OrganizationData} organizationData
   * @returns {object} - the new sub organization
   */
  createOrganization = (cancelSignal, organizationData) => this.requestPost('organizations', organizationData, cancelSignal);
  getOrganizations = (cancelSignal) => this.requestGet('organizations', null, cancelSignal);
  getOrganization = (cancelSignal, orgId) => {
    return this.requestGet(`organizations/${orgId}`, null, cancelSignal);
  };
  deleteOrganization = (cancelSignal, orgId) => this.requestDelete(`organizations/${orgId}`, null, cancelSignal);
  updateOrganization = (cancelSignal, orgId, OrganizationData) => this.requestPut(`organizations/${orgId}`, OrganizationData, cancelSignal);

  // Organzation templates are used to create new organizations and contain the default privileges, default roles,
  // and also which models, dashboards, alarms, providers that shall be linked to the new organization.
  getOrganizationTemplates = (cancelSignal) => this.requestGet('organization-templates', null, cancelSignal);
  getOrganizationTemplate = (cancelSignal, templateId) => this.requestGet(`organization-templates/${templateId}`, null, cancelSignal);
  createOrganizationTemplate = (cancelSignal, templateData) => this.requestPost('organization-templates', templateData, cancelSignal);
  deleteOrganizationTemplate = (cancelSignal, templateId) => this.requestDelete(`organization-templates/${templateId}`, null, cancelSignal);
  updateOrganizationTemplate = (cancelSignal, templateId, templateData) => this.requestPut(`organization-templates/${templateId}`, templateData, cancelSignal);

  // users exists in the organization, but if they exists in many they will be linked to the same user in the Azure B2C
  getOrganizationUsers = (cancelSignal, orgId) => this.requestGet(`organizations/${orgId}/users`, null, cancelSignal);
  getOrganizationUser = (cancelSignal, orgId, userId) => this.requestGet(`organizations/${orgId}/users/${userId}`, null, cancelSignal);
  createOrganizationUser = (cancelSignal, orgId, userData) => this.requestPost(`organizations/${orgId}/users`, userData, cancelSignal);
  deleteOrganizationUser = (cancelSignal, orgId, userId) => this.requestDelete(`organizations/${orgId}/users/${userId}`, null, cancelSignal);
  updateOrganizationUser = (cancelSignal, orgId, userId, userData) => this.requestPatch(`organizations/${orgId}/users/${userId}`, userData, cancelSignal);


  getGroups = (cancelSignal) => this.requestGet('groups', null, cancelSignal);
  getGroup = (cancelSignal, groupId) => this.requestGet(`groups/${groupId}`, null, cancelSignal);
  createGroup = (cancelSignal, groupData) => this.requestPost('groups', groupData, cancelSignal);
  deleteGroup = (cancelSignal, groupId) => this.requestDelete(`groups/${groupId}`, null, cancelSignal);
  updateGroup = (cancelSignal, groupId, groupData) => this.requestPut(`groups/${groupId}`, groupData, cancelSignal);


  /**
   * Add an UGO relation to a resource
   * @param {ResourceType} resourceType - the type of resource
   * @param {string} resourceId - the id of the resource
   * @param {Relationship} relationship - the relationship to add
   */
  addResourceRelation = (cancelSignal, resourceType, resourceId, relationship) => this.requestPost(`${resourceType}/${resourceId}/relationships`, relationship, cancelSignal);
  updateResourceRelation = (cancelSignal, resourceType, resourceId, relationship) => this.requestPatch(`${resourceType}/${resourceId}/relationships`, relationship, cancelSignal);
  deleteResourceRelation = (cancelSignal, resourceType, resourceId, relationship) => this.requestDelete(`${resourceType}/${resourceId}/relationships`, relationship, cancelSignal);


  getRoles = (cancelSignal) => this.requestGet('roles', null, cancelSignal);
  getRole = (cancelSignal, roleId) => this.requestGet(`roles/${roleId}`, null, cancelSignal);
  createRole = (cancelSignal, roleData) => this.requestPost('roles', roleData, cancelSignal);
  deleteRole = (cancelSignal, roleId) => this.requestDelete(`roles/${roleId}`, null, cancelSignal);
  updateRole = (cancelSignal, roleId, roleData) => this.requestPut(`roles/${roleId}`, roleData, cancelSignal);

  // Privilages are a list and cannot be manipulated individually. They are initially created when the suborg is created
  getPrivileges = (cancelSignal) => this.requestGet('privileges', null, cancelSignal);
  updatePrivilages = (cancelSignal, privilegeData) => this.requestPut('privileges', privilegeData, cancelSignal);
}

export default OrganizationApi;
